import React, { useRef, useState } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { Button } from "./ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "./ui/dialog";
import { Upload, FileDown, Loader2 } from "lucide-react";
import { toast } from "sonner";

export function BulkUploadButton({ uploadUrl, templateUrl, title, description, onDone, testId = "bulk-upload" }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);

  const downloadTemplate = async () => {
    try {
      const res = await api.get(templateUrl, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = templateUrl.split("/").pop();
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not download template");
    }
  };

  const upload = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) { toast.error("Choose a .csv or .xlsx file first"); return; }
    setBusy(true); setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post(uploadUrl, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(res.data);
      toast.success(`${res.data.inserted} row(s) imported`);
      onDone && onDone();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button variant="outline" className="rounded-none gap-2" onClick={() => { setResult(null); setOpen(true); }} data-testid={`${testId}-btn`}>
        <Upload className="w-4 h-4" /> Import
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none">
          <DialogHeader>
            <DialogTitle className="font-display">{title}</DialogTitle>
            <DialogDescription>{description}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <button type="button" onClick={downloadTemplate} className="flex items-center gap-2 text-sm text-primary hover:underline" data-testid={`${testId}-template`}>
              <FileDown className="w-4 h-4" /> Download CSV template
            </button>
            <input ref={fileRef} type="file" accept=".csv,.xlsx,.xlsm" data-testid={`${testId}-file`}
              className="block w-full text-sm border border-border rounded-none file:mr-3 file:border-0 file:bg-secondary file:px-3 file:py-2 file:text-sm" />
            {result && (
              <div className="border border-border p-3 text-sm font-mono" data-testid={`${testId}-result`}>
                <div className="text-success">Imported: {result.inserted}</div>
                {result.skipped > 0 && <div className="text-destructive">Skipped: {result.skipped}</div>}
                {(result.errors || []).slice(0, 5).map((e, i) => (
                  <div key={i} className="text-xs text-muted-foreground">row {e.row}: {e.error}</div>
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button className="rounded-none gap-2" onClick={upload} disabled={busy} data-testid={`${testId}-submit`}>
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Upload
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
